import type { UsersStatsResponse, UserStats, UserTrack } from './types.js';
import { getElement } from './dom.js';
// Spootify Users Statistics & Leaderboard
import { getAuthHeaders } from './auth.js';

export function getUserAvatar(username: string | null) {
  const seed = encodeURIComponent((username || 'user').trim().toLowerCase());
  return `/avatars/${seed}.svg`;
}

function escapeHtml(str: unknown) {
  if (!str) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function formatNumber(num: number) {
  return new Intl.NumberFormat('fr-FR').format(num || 0);
}

function formatDateAgo(dateStr: string | null) {
  if (!dateStr) return 'Jamais';
  try {
    const d = new Date(dateStr.replace(' ', 'T') + 'Z');
    if (isNaN(d.getTime())) return dateStr;
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHours = Math.floor(diffMin / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMin < 1) return "À l'instant";
    if (diffMin < 60) return `Il y a ${diffMin} min`;
    if (diffHours < 24) return `Il y a ${diffHours} h`;
    if (diffDays === 1) return 'Hier';
    if (diffDays < 30) return `Il y a ${diffDays} jours`;
    return d.toLocaleDateString('fr-FR');
  } catch {
    return dateStr;
  }
}

// State
let statsData: UsersStatsResponse | null = null;
let currentSort = 'totalRated-desc';
let selectedUser: string | null = null;
let selectedTab = 'all';
let userTracksCache: UserTrack[] = [];

// DOM Elements
const kpiTotalTracks = getElement('kpi-total-tracks', HTMLElement);
const kpiLocalTracks = getElement('kpi-local-tracks', HTMLElement);
const kpiTotalRatings = getElement('kpi-total-ratings', HTMLElement);
const kpiTotalLikes = getElement('kpi-total-likes', HTMLElement);
const usersStatsGrid = getElement('users-stats-grid', HTMLElement);
const sortSelect = getElement('sort-select', HTMLSelectElement);

const activeUserChip = getElement('active-user-chip', HTMLElement);
const statsUserAvatar = getElement('stats-user-avatar', HTMLImageElement);
const statsUserName = getElement('stats-user-name', HTMLElement);

// Modal Elements
const userTracksModal = getElement('user-tracks-modal', HTMLElement);
const modalUserAvatar = getElement('modal-user-avatar', HTMLImageElement);
const modalUserTitle = getElement('modal-user-title', HTMLElement);
const modalUserStatsSub = getElement('modal-user-stats-sub', HTMLElement);
const btnCloseTracksModal = getElement('btn-close-tracks-modal', HTMLButtonElement);
const btnSwitchToUser = getElement('btn-switch-to-user', HTMLButtonElement);
const userTracksSearch = getElement('user-tracks-search', HTMLInputElement);
const userTracksList = getElement('user-tracks-list', HTMLElement);

const tabAllUserTracks = getElement('tab-all-user-tracks', HTMLButtonElement);
const tabLikedUserTracks = getElement('tab-liked-user-tracks', HTMLButtonElement);
const tabDislikedUserTracks = getElement('tab-disliked-user-tracks', HTMLButtonElement);

const userTabCountAll = getElement('user-tab-count-all', HTMLElement);
const userTabCountLiked = getElement('user-tab-count-liked', HTMLElement);
const userTabCountDisliked = getElement('user-tab-count-disliked', HTMLElement);

// Initialize
async function init() {
  updateHeaderActiveUser();
  await loadStats();

  if (sortSelect) {
    sortSelect.addEventListener('change', (e) => {
      currentSort = sortSelect.value;
      renderUsersGrid();
    });
  }

  if (btnCloseTracksModal) {
    btnCloseTracksModal.addEventListener('click', hideTracksModal);
  }

  if (userTracksModal) {
    userTracksModal.addEventListener('click', (e) => {
      if (e.target === userTracksModal) hideTracksModal();
    });
  }

  [tabAllUserTracks, tabLikedUserTracks, tabDislikedUserTracks].forEach(tabBtn => {
    if (!tabBtn) return;
    tabBtn.addEventListener('click', () => {
      document.querySelectorAll('.modal-actions-bar .modal-tab-btn').forEach(b => b.classList.remove('active'));
      tabBtn.classList.add('active');
      selectedTab = tabBtn.getAttribute('data-tab') || 'all';
      filterAndRenderUserTracks();
    });
  });

  if (userTracksSearch) {
    userTracksSearch.addEventListener('input', () => {
      filterAndRenderUserTracks();
    });
  }

  if (btnSwitchToUser) {
    btnSwitchToUser.addEventListener('click', () => {
      if (selectedUser) {
        localStorage.setItem('spootify_user', selectedUser);
        window.location.href = '/';
      }
    });
  }
}

function updateHeaderActiveUser() {
  const currentSaved = localStorage.getItem('spootify_user');
  if (currentSaved && activeUserChip && statsUserAvatar && statsUserName) {
    statsUserAvatar.src = getUserAvatar(currentSaved);
    statsUserAvatar.alt = currentSaved;
    statsUserName.textContent = currentSaved.charAt(0).toUpperCase() + currentSaved.slice(1);
    activeUserChip.style.display = 'inline-flex';
  }
}

async function loadStats() {
  try {
    const res = await fetch('/api/users-stats', {
      headers: { ...getAuthHeaders() }
    });
    if (!res.ok) throw new Error('Erreur lors du chargement des statistiques');
    const data: UsersStatsResponse = await res.json();
    statsData = data;

    // Populate KPI
    if (kpiTotalTracks) kpiTotalTracks.textContent = formatNumber(data.totalTracks);
    if (kpiLocalTracks) kpiLocalTracks.textContent = formatNumber(data.localTracks);
    if (kpiTotalRatings) kpiTotalRatings.textContent = formatNumber(data.totalRatingsAllUsers);
    if (kpiTotalLikes) kpiTotalLikes.textContent = formatNumber(data.totalLikesAllUsers);

    renderUsersGrid();
  } catch (err) {
    if (usersStatsGrid) {
      usersStatsGrid.innerHTML = `<div class="error-box">⚠️ Impossible de charger les statistiques : ${escapeHtml(err instanceof Error ? err.message : String(err))}</div>`;
    }
  }
}

function sortUsers(usersList: UserStats[]) {
  const list = [...usersList];
  switch (currentSort) {
    case 'totalRated-desc':
      return list.sort((a, b) => b.totalRated - a.totalRated || b.liked - a.liked);
    case 'liked-desc':
      return list.sort((a, b) => b.liked - a.liked || b.totalRated - a.totalRated);
    case 'disliked-desc':
      return list.sort((a, b) => b.disliked - a.disliked || b.totalRated - a.totalRated);
    case 'likeRatio-desc':
      return list.sort((a, b) => b.likeRatio - a.likeRatio || b.totalRated - a.totalRated);
    case 'userId-asc':
      return list.sort((a, b) => a.userId.localeCompare(b.userId));
    default:
      return list;
  }
}

function renderUsersGrid() {
  if (!statsData || !usersStatsGrid) return;

  const sortedUsers = sortUsers(statsData.users);

  if (sortedUsers.length === 0) {
    usersStatsGrid.innerHTML = `<div class="empty-state">Aucun utilisateur trouvé</div>`;
    return;
  }

  const cardsHtml = sortedUsers.map((user, idx) => {
    const isInactive = user.totalRated === 0;
    let rankBadge = '';
    let rankClass = '';

    if (isInactive) {
      rankBadge = '—';
      rankClass = 'card-inactive';
    } else if (currentSort.startsWith('totalRated') || currentSort.startsWith('liked')) {
      if (idx === 0) {
        rankBadge = '🥇';
        rankClass = 'rank-gold';
      } else if (idx === 1) {
        rankBadge = '🥈';
        rankClass = 'rank-silver';
      } else if (idx === 2) {
        rankBadge = '🥉';
        rankClass = 'rank-bronze';
      } else {
        rankBadge = `#${idx + 1}`;
      }
    } else {
      rankBadge = `#${idx + 1}`;
    }

    const displayName = user.userId.charAt(0).toUpperCase() + user.userId.slice(1);
    const likePercent = user.totalRated > 0 ? Math.round((user.liked / user.totalRated) * 100) : 0;
    const dislikePercent = user.totalRated > 0 ? (100 - likePercent) : 0;
    const poolProgress = Math.min(100, user.completionPercentage);

    return `
      <div class="user-stat-card ${rankClass}" data-user="${escapeHtml(user.userId)}">
        <div class="user-stat-card-header">
          <div class="user-info-group">
            <img class="user-card-avatar" src="${getUserAvatar(user.userId)}" alt="${escapeHtml(user.userId)}" loading="lazy" />
            <div class="user-name-box">
              <h3 class="user-name-title">${escapeHtml(displayName)}</h3>
              <span class="user-last-active">${isInactive ? 'Aucun morceau évalué' : `Dernier tri : ${escapeHtml(formatDateAgo(user.lastRatedAt))}`}</span>
            </div>
          </div>
          <div class="rank-pill">${rankBadge}</div>
        </div>

        <div class="user-score-highlight">
          <div class="score-number">${formatNumber(user.totalRated)}</div>
          <div class="score-label">musiques triées au total</div>
        </div>

        <div class="user-metric-bars">
          <div class="metric-row">
            <span class="metric-tag like-tag">❤️ ${formatNumber(user.liked)} likes (${likePercent}%)</span>
            <span class="metric-tag dislike-tag">👎 ${formatNumber(user.disliked)} dislikes (${dislikePercent}%)</span>
          </div>
          <div class="user-progress-bar-bg" title="${likePercent}% likes / ${dislikePercent}% dislikes">
            <div class="user-bar-liked" style="width: ${likePercent}%"></div>
            <div class="user-bar-disliked" style="width: ${dislikePercent}%"></div>
          </div>
        </div>

        <div class="catalog-coverage-box">
          <div class="coverage-label">
            <span>Progression catalogue</span>
            <span class="coverage-val">${user.completionPercentage}%</span>
          </div>
          <div class="coverage-bar-bg">
            <div class="coverage-bar-fill" style="width: ${poolProgress}%"></div>
          </div>
        </div>

        <button class="btn-view-user-tracks" data-user="${escapeHtml(user.userId)}">
          <span>${isInactive ? '⏳ Aucun morceau trié' : '🎵 Voir les musiques triées'}</span>
          <span class="arrow">➔</span>
        </button>
      </div>
    `;
  }).join('');

  usersStatsGrid.innerHTML = cardsHtml;

  // Add click listeners
  usersStatsGrid.querySelectorAll('.btn-view-user-tracks, .user-stat-card').forEach(elem => {
    elem.addEventListener('click', (e) => {
      const username = elem.getAttribute('data-user') || elem.closest('.user-stat-card')?.getAttribute('data-user');
      if (username) {
        openUserTracksModal(username);
      }
    });
  });
}

// User Tracks Modal Handling
async function openUserTracksModal(username: string) {
  selectedUser = username;
  selectedTab = 'all';
  userTracksCache = [];

  const userStats = statsData?.users?.find(u => u.userId === username);
  const displayName = username.charAt(0).toUpperCase() + username.slice(1);

  if (modalUserAvatar) modalUserAvatar.src = getUserAvatar(username);
  if (modalUserTitle) modalUserTitle.textContent = `Musiques triées par ${displayName}`;
  if (modalUserStatsSub && userStats) {
    modalUserStatsSub.innerHTML = `
      <span>${formatNumber(userStats.totalRated)} musiques triées</span> • 
      <span style="color: var(--like-color);">❤️ ${formatNumber(userStats.liked)}</span> • 
      <span style="color: var(--dislike-color);">👎 ${formatNumber(userStats.disliked)}</span> • 
      <span>${userStats.completionPercentage}% du catalogue</span>
    `;
  }

  // Update tabs counters
  if (userTabCountAll && userStats) userTabCountAll.textContent = formatNumber(userStats.totalRated);
  if (userTabCountLiked && userStats) userTabCountLiked.textContent = formatNumber(userStats.liked);
  if (userTabCountDisliked && userStats) userTabCountDisliked.textContent = formatNumber(userStats.disliked);

  // Reset active tab button
  document.querySelectorAll('.modal-actions-bar .modal-tab-btn').forEach(b => {
    b.classList.toggle('active', b.getAttribute('data-tab') === 'all');
  });

  if (userTracksSearch) userTracksSearch.value = '';
  if (userTracksList) {
    userTracksList.innerHTML = `<div class="loading-state">Chargement des morceaux de ${escapeHtml(displayName)}...</div>`;
  }

  if (userTracksModal) userTracksModal.classList.add('active');

  // Fetch tracks
  try {
    const res = await fetch(`/api/user-tracks?user=${encodeURIComponent(username)}&status=all&limit=500`, {
      headers: { ...getAuthHeaders() }
    });
    if (!res.ok) throw new Error('Erreur lors du chargement des morceaux');
    const data: { tracks: UserTrack[] } = await res.json();
    userTracksCache = data.tracks || [];
    filterAndRenderUserTracks();
  } catch (err) {
    if (userTracksList) {
      userTracksList.innerHTML = `<div class="error-box">⚠️ Impossible de charger les musiques : ${escapeHtml(err instanceof Error ? err.message : String(err))}</div>`;
    }
  }
}

function hideTracksModal() {
  if (userTracksModal) userTracksModal.classList.remove('active');
  // Stop any playing audio inside modal
  const playingAudios = userTracksList?.querySelectorAll('audio');
  playingAudios?.forEach(a => a.pause());
}

function filterAndRenderUserTracks() {
  if (!userTracksList) return;

  const searchQuery = (userTracksSearch?.value || '').trim().toLowerCase();

  let filtered = userTracksCache.filter(item => {
    if (selectedTab !== 'all' && item.status !== selectedTab) {
      return false;
    }
    if (searchQuery) {
      const matchTitle = (item.title || '').toLowerCase().includes(searchQuery);
      const matchArtist = (item.artist || '').toLowerCase().includes(searchQuery);
      const matchAlbum = (item.album || '').toLowerCase().includes(searchQuery);
      return matchTitle || matchArtist || matchAlbum;
    }
    return true;
  });

  if (filtered.length === 0) {
    userTracksList.innerHTML = `
      <div class="empty-state">
        <div style="font-size: 2rem; margin-bottom: 8px;">🎵</div>
        <div>Aucun morceau trouvé pour cette sélection.</div>
      </div>
    `;
    return;
  }

  userTracksList.innerHTML = filtered.map(track => {
    const isLike = track.status === 'like';
    const statusPill = isLike
      ? `<span class="badge-status badge-like">👍 Like</span>`
      : `<span class="badge-status badge-dislike">👎 Dislike</span>`;

    const sourceBadge = track.isLocal
      ? `<span class="track-badge-local">💾 Local Audio</span>`
      : `<span class="track-badge-yt">🌐 YouTube</span>`;

    return `
      <div class="user-track-row ${isLike ? 'row-liked' : 'row-disliked'}">
        <div class="track-row-status-icon">${isLike ? '❤️' : '👎'}</div>
        <div class="track-row-info">
          <div class="track-row-title">${escapeHtml(track.title || 'Titre inconnu')}</div>
          <div class="track-row-artist">${escapeHtml(track.artist || 'Artiste inconnu')} ${track.album ? `• <em>${escapeHtml(track.album)}</em>` : ''}</div>
          <div class="track-row-meta">
            ${statusPill}
            ${sourceBadge}
            <span class="track-row-date">Trié ${formatDateAgo(track.updatedAt || track.createdAt)}</span>
          </div>
        </div>
        <div class="track-row-actions">
          ${track.isLocal && track.audioUrl ? `
            <audio controls preload="none" src="${escapeHtml(track.audioUrl)}" class="mini-audio-player"></audio>
          ` : ''}
          <a href="https://www.youtube.com/watch?v=${encodeURIComponent(track.videoId)}" target="_blank" rel="noopener" class="btn-yt-link" title="Ouvrir sur YouTube">
            <span>YouTube ↗</span>
          </a>
        </div>
      </div>
    `;
  }).join('');
}

// Start
init();
